import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Check, Sparkles, MapPin, Heart, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";

const HOME_PARKS = [
  "Walt Disney World",
  "Disneyland Resort",
  "Disneyland Paris",
  "Tokyo Disney Resort",
  "Shanghai Disney Resort",
  "Hong Kong Disneyland",
  "Disney Cruise Line",
  "Aulani",
];

const INTERESTS = [
  "Trip Planning",
  "Photography",
  "Pin Trading",
  "Food & Dining",
  "Rides & Attractions",
  "Resort Hopping",
  "Runs / runDisney",
  "Festivals",
  "Annual Passholder",
  "Disney History",
  "Cosplay & Bounding",
  "Cast Member Life",
  "Merchandise",
  "DVC",
];

type SuggestedCreator = {
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  tier: string;
};

export function OnboardingWizard() {
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [homePark, setHomePark] = useState<string | null>(null);
  const [interests, setInterests] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<SuggestedCreator[]>([]);
  const [follows, setFollows] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Detect logged-in user and onboarding status
  useEffect(() => {
    let cancelled = false;

    const check = async (uid: string) => {
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarded_at, home_park, interests")
        .eq("id", uid)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        logger.error("onboarding profile fetch failed", { component: "Onboarding", additionalData: { error: String(error) } });
        return;
      }
      if (!data?.onboarded_at) {
        setHomePark(data?.home_park ?? null);
        setInterests((data?.interests as string[]) ?? []);
        setOpen(true);
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id ?? null;
      setUserId(uid);
      if (uid) void check(uid);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      if (uid && (event === "SIGNED_IN" || event === "USER_UPDATED")) {
        void check(uid);
      }
      if (!uid) setOpen(false);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Load suggested creators when reaching the follow step
  useEffect(() => {
    if (step !== 2 || suggested.length > 0) return;
    (async () => {
      const { data, error } = await supabase
        .from("creator_profiles")
        .select("user_id, handle, display_name, avatar_url, tier, niches")
        .order("tier", { ascending: true })
        .limit(12);
      if (error) {
        logger.error("creator suggestions failed", { component: "Onboarding", additionalData: { error: String(error) } });
        return;
      }
      // Rank: prefer creators whose niches intersect interests
      const ranked = (data ?? [])
        .map((c: any) => ({
          ...c,
          score:
            (Array.isArray(c.niches)
              ? c.niches.filter((n: string) =>
                  interests.some((i) =>
                    i.toLowerCase().includes(String(n).toLowerCase()) ||
                    String(n).toLowerCase().includes(i.toLowerCase())
                  )
                ).length
              : 0) +
            (c.tier === "partner" ? 3 : c.tier === "verified" ? 2 : 1),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);
      setSuggested(ranked);
    })();
  }, [step, interests, suggested.length]);

  const toggleInterest = (i: string) => {
    setInterests((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };
  const toggleFollow = (uid: string) => {
    setFollows((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const progress = useMemo(() => ((step + 1) / 3) * 100, [step]);

  const finish = async (skipFollows = false) => {
    if (!userId) return;
    setSaving(true);
    try {
      const { error: profErr } = await supabase
        .from("profiles")
        .update({
          home_park: homePark,
          interests,
          onboarded_at: new Date().toISOString(),
        })
        .eq("id", userId);
      if (profErr) throw profErr;

      if (!skipFollows && follows.size > 0) {
        const rows = Array.from(follows).map((target) => ({
          follower_id: userId,
          following_id: target,
        }));
        const { error: followErr } = await supabase
          .from("followers")
          .upsert(rows, { onConflict: "follower_id,following_id" });
        if (followErr) logger.error("onboarding follow upsert failed", { component: "Onboarding", additionalData: { error: String(followErr) } });
      }

      toast({
        title: "Welcome aboard!",
        description: "Your feed is now personalized. Magic awaits.",
      });
      setOpen(false);
    } catch (e: any) {
      logger.error("onboarding save failed", { component: "Onboarding", additionalData: { error: String(e) } });
      toast({
        title: "Couldn't save preferences",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!userId) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Welcome to the community
          </DialogTitle>
          <DialogDescription>
            Three quick steps to personalize your feed.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" /> Pick your home park
            </div>
            <div className="grid grid-cols-2 gap-2">
              {HOME_PARKS.map((p) => (
                <Card
                  key={p}
                  onClick={() => setHomePark(p)}
                  className={`cursor-pointer p-3 text-sm transition border ${
                    homePark === p
                      ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                      : "hover:border-primary/40"
                  }`}
                >
                  {p}
                </Card>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Heart className="h-4 w-4" /> What are you into? (pick a few)
            </div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => {
                const active = interests.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleInterest(i)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition ${
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    {active && <Check className="mr-1 inline h-3 w-3" />}
                    {i}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" /> Follow a few creators to get started
            </div>
            {suggested.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No creators to suggest yet — you can find them later under{" "}
                <span className="font-medium">Creators</span>.
              </p>
            ) : (
              <div className="grid gap-2 max-h-72 overflow-y-auto pr-1">
                {suggested.map((c) => {
                  const following = follows.has(c.user_id);
                  return (
                    <div
                      key={c.user_id}
                      className="flex items-center gap-3 rounded-lg border p-2"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={c.avatar_url ?? undefined} />
                        <AvatarFallback>
                          {(c.display_name || c.handle).slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {c.display_name || c.handle}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="truncate">@{c.handle}</span>
                          <Badge variant="secondary" className="h-4 text-[10px]">
                            {c.tier}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={following ? "secondary" : "default"}
                        onClick={() => toggleFollow(c.user_id)}
                      >
                        {following ? "Following" : "Follow"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            onClick={() => (step === 0 ? finish(true) : setStep(step - 1))}
            disabled={saving}
          >
            {step === 0 ? "Skip" : "Back"}
          </Button>
          {step < 2 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={(step === 0 && !homePark) || saving}
            >
              Continue
            </Button>
          ) : (
            <Button onClick={() => finish(false)} disabled={saving}>
              {saving ? "Saving…" : "Finish"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingWizard;
